import { DataTable } from "@/components/admin/data-table";
import { ExportButton } from "@/components/admin/export-button";
import { FilterButton } from "@/components/admin/filter-form";
import { List } from "@/components/admin/list";
import { ListPagination } from "@/components/admin/list-pagination";
import { SearchInput } from "@/components/admin/search-input";
import { SelectInput } from "@/components/admin/select-input";
import { SortButton } from "@/components/admin/sort-button";
import { Badge } from "@/components/ui/badge";
import { useIsMobile } from "@/hooks/use-mobile";
import { Building2, ExternalLink } from "lucide-react";
import {
  RecordContextProvider,
  useListContext,
  useRecordContext,
  useTranslate,
} from "ra-core";
import { Link } from "react-router";

import { TopToolbar } from "../layout/TopToolbar";
import type { AuditResearchDirectoryEntry } from "../types";
import { SroMembershipBadge } from "../companies/CompanySalesIntelligence";

const KIND_CHOICES = [
  "manager",
  "auditor",
  "expert",
  "company_social",
  "expert_social",
  "community",
].map((kind) => ({
  id: kind,
  name: `resources.audit_research_directory.kinds.${kind}`,
}));

const PLATFORM_CHOICES = [
  "telegram",
  "telegram_chat",
  "vk",
  "max",
  "youtube",
  "rutube",
  "ok",
  "linkedin",
  "dzen",
  "tenchat",
  "e-xecutive",
  "companies.rbc.ru",
].map((platform) => ({ id: platform, name: platform }));

const CONFIDENCE_CHOICES = ["high", "medium", "low"].map((confidence) => ({
  id: confidence,
  name: `resources.audit_research_directory.confidence.${confidence}`,
}));

const EMPLOYMENT_CHOICES = [
  {
    id: "Основное",
    name: "resources.audit_research_directory.employment.primary",
  },
  {
    id: "Совместительство",
    name: "resources.audit_research_directory.employment.part_time",
  },
];

const filters = [
  <SearchInput source="q" alwaysOn key="search" />,
  <SelectInput source="record_kind" choices={KIND_CHOICES} key="record_kind" />,
  <SelectInput source="platform" choices={PLATFORM_CHOICES} key="platform" />,
  <SelectInput
    source="confidence"
    choices={CONFIDENCE_CHOICES}
    key="confidence"
  />,
  <SelectInput
    source="employment_type"
    choices={EMPLOYMENT_CHOICES}
    key="employment_type"
  />,
];

const SORT_FIELDS = [
  "record_kind",
  "name",
  "company_name",
  "platform",
  "confidence",
  "checked_at",
];

const DirectoryActions = () => (
  <TopToolbar>
    <FilterButton />
    <SortButton fields={SORT_FIELDS} />
    <ExportButton />
  </TopToolbar>
);

export const AuditResearchDirectoryList = () => {
  const translate = useTranslate();
  return (
    <List
      title={translate("resources.audit_research_directory.title")}
      filters={filters}
      actions={<DirectoryActions />}
      perPage={50}
      sort={{ field: "record_kind", order: "ASC" }}
      pagination={<ListPagination rowsPerPageOptions={[25, 50, 100, 250]} />}
    >
      <DirectoryContent />
    </List>
  );
};

const DirectoryContent = () => {
  const isMobile = useIsMobile();
  return isMobile ? <DirectoryMobileCards /> : <DirectoryTable />;
};

const DirectoryTable = () => (
  <div className="overflow-x-auto">
    <DataTable>
      <DataTable.Col source="record_kind">
        <KindField />
      </DataTable.Col>
      <DataTable.Col source="name">
        <PersonOrChannelField />
      </DataTable.Col>
      <DataTable.Col source="company_name">
        <CompanyField />
      </DataTable.Col>
      <DataTable.Col source="platform" />
      <DataTable.Col source="contact_url">
        <PrimaryLinkField />
      </DataTable.Col>
      <DataTable.Col source="confidence">
        <ConfidenceField />
      </DataTable.Col>
      <DataTable.Col source="checked_at" />
      <DataTable.Col label="resources.audit_research_directory.fields.source_url">
        <SourceLinkField />
      </DataTable.Col>
    </DataTable>
  </div>
);

const DirectoryMobileCards = () => {
  const { data, isPending } = useListContext<AuditResearchDirectoryEntry>();
  if (isPending) return null;

  return (
    <div className="divide-y">
      {(data ?? []).map((entry) => (
        <RecordContextProvider key={entry.id} value={entry}>
          <article className="space-y-2 py-4">
            <div className="flex items-start justify-between gap-2">
              <PersonOrChannelField />
              <KindField />
            </div>
            <CompanyField />
            <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              {entry.platform ? (
                <Badge variant="outline">{entry.platform}</Badge>
              ) : null}
              <ConfidenceField />
              <span>{entry.checked_at}</span>
            </div>
            <div className="flex flex-wrap gap-3 text-sm">
              <PrimaryLinkField />
              <SourceLinkField />
            </div>
          </article>
        </RecordContextProvider>
      ))}
    </div>
  );
};

export const KindField = () => {
  const record = useRecordContext<AuditResearchDirectoryEntry>();
  const translate = useTranslate();
  if (!record) return null;
  return (
    <Badge variant="secondary">
      {translate(
        `resources.audit_research_directory.kinds.${record.record_kind}`,
      )}
    </Badge>
  );
};

const PersonOrChannelField = () => {
  const record = useRecordContext<AuditResearchDirectoryEntry>();
  if (!record) return null;
  return (
    <div className="min-w-52">
      <div className="font-medium">{record.name}</div>
      {record.role_or_audience ? (
        <div className="max-w-96 whitespace-normal text-xs text-muted-foreground">
          {record.role_or_audience}
        </div>
      ) : null}
      {record.certificate_number ? (
        <div className="text-xs text-muted-foreground">
          {record.certificate_number}
          {record.employment_type ? ` · ${record.employment_type}` : ""}
        </div>
      ) : null}
    </div>
  );
};

const CompanyField = () => {
  const record = useRecordContext<AuditResearchDirectoryEntry>();
  if (!record?.company_name) return null;
  const membershipStatus = record.metadata?.company_sro_membership_status;
  const ceased = membershipStatus === "ceased";
  const company =
    record.company_id == null ? (
      <span>{record.company_name}</span>
    ) : (
      <Link
        to={`/companies/${record.company_id}/show`}
        className="inline-flex min-w-40 items-center gap-1 underline hover:no-underline"
      >
        <Building2 className="h-3.5 w-3.5 shrink-0" />
        <span className="line-clamp-2">{record.company_name}</span>
      </Link>
    );

  return (
    <div className="flex min-w-40 flex-col items-start gap-1">
      {company}
      {ceased ? <SroMembershipBadge status="ceased" /> : null}
    </div>
  );
};

const PrimaryLinkField = () => {
  const record = useRecordContext<AuditResearchDirectoryEntry>();
  const translate = useTranslate();
  if (!record?.contact_url) return null;
  return (
    <a
      href={record.contact_url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 underline hover:no-underline"
    >
      <ExternalLink className="h-3.5 w-3.5" />
      {translate("resources.audit_research_directory.open")}
    </a>
  );
};

const SourceLinkField = () => {
  const record = useRecordContext<AuditResearchDirectoryEntry>();
  const translate = useTranslate();
  if (!record?.source_url) return null;
  return (
    <a
      href={record.source_url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 underline hover:no-underline"
    >
      <ExternalLink className="h-3.5 w-3.5" />
      {translate("resources.audit_research_directory.source")}
    </a>
  );
};

const ConfidenceField = () => {
  const record = useRecordContext<AuditResearchDirectoryEntry>();
  const translate = useTranslate();
  if (!record) return null;
  return (
    <Badge variant="outline">
      {translate(
        `resources.audit_research_directory.confidence.${record.confidence}`,
      )}
    </Badge>
  );
};
